import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { cn } from '../cn.js'

export interface ComboboxProps {
  label: string
  options: string[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  hint?: ReactNode
  error?: string | undefined
  required?: boolean
  disabled?: boolean
  name?: string
  /** Copy for the "nothing matched" row, so the funnel owns the wording. */
  emptyText?: string
}

/**
 * A type-to-filter picker (PLAN.md §9.5, §27.3).
 *
 * `Select` argues for the native control and is right nearly everywhere. This
 * is the exception it names: Thailand's AML declaration asks for one of **77
 * provinces**, and a native picker at that length is a scroll through a list
 * nobody can scan. Typing three letters is the difference between a five-second
 * answer and a thirty-second one, on the step where the form is already long.
 *
 * It stays close to the platform anyway — a real `<input>`, so mobile keyboards,
 * autofill and zoom behave — and it never traps a value: the field is committed
 * only when it matches an option, and reverts on blur when it does not, so the
 * submitted value is always one the CRM offered.
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  placeholder,
  hint,
  error,
  required,
  disabled,
  name,
  emptyText = 'No matches',
}: ComboboxProps) {
  const id = useId()
  const listId = `${id}-list`
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const [query, setQuery] = useState(value)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)

  // A value chosen elsewhere — restored from the store on resume, or cleared
  // when the form resets — has to show up in the input.
  useEffect(() => {
    setQuery(value)
  }, [value])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    // An exact match means the user has already chosen: show the whole list so
    // reopening the field is not a one-item dead end.
    if (!needle || options.some((o) => o.toLowerCase() === needle)) return options
    return options.filter((o) => o.toLowerCase().includes(needle))
  }, [options, query])

  function commit(option: string) {
    onChange(option)
    setQuery(option)
    setOpen(false)
    setActive(0)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) => (matches.length === 0 ? 0 : (i + step + matches.length) % matches.length))
      return
    }
    if (event.key === 'Enter' && open) {
      const choice = matches[active]
      if (choice) {
        // Only swallowed when it selects something; otherwise the form submits,
        // which is what a keyboard user expects from a field they have finished.
        event.preventDefault()
        commit(choice)
      }
      return
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault()
      setQuery(value)
      setOpen(false)
    }
  }

  function onBlur(event: React.FocusEvent<HTMLDivElement>) {
    // Clicking an option moves focus inside the widget; only a departure counts.
    if (rootRef.current?.contains(event.relatedTarget as Node | null)) return
    setOpen(false)
    // Free text is not an option the CRM offered, so it cannot be the answer.
    if (query !== value) setQuery(value)
  }

  return (
    <div className="flex flex-col gap-1.5" ref={rootRef} onBlur={onBlur}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <div className="relative">
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && matches[active] ? `${id}-opt-${active}` : undefined}
          autoComplete="off"
          value={query}
          disabled={disabled}
          required={required}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            setActive(0)
            // Typing over a chosen value clears it: the field must not keep
            // reporting a province the input no longer shows.
            if (value) onChange('')
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className={cn(
            'min-h-[44px] w-full rounded-lg border bg-white px-3 py-2 text-ink',
            'placeholder:text-muted-soft',
            error ? 'border-danger' : 'border-line-strong',
            'disabled:bg-surface-alt disabled:text-muted',
          )}
        />
        {/* The committed value, so this works inside a plain form submit too. */}
        {name && <input type="hidden" name={name} value={value} />}

        {open && (
          <ul
            id={listId}
            role="listbox"
            aria-label={label}
            className={cn(
              'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-line-strong',
              'bg-white py-1 shadow-lg',
            )}
          >
            {matches.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted" role="presentation">
                {emptyText}
              </li>
            )}
            {matches.map((option, index) => (
              <li
                key={option}
                id={`${id}-opt-${index}`}
                role="option"
                aria-selected={option === value}
                // Pointer-down rather than click: click fires after blur, which
                // would have already reverted the input.
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(option)
                }}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm text-ink',
                  index === active && 'bg-surface-alt',
                  option === value && 'font-medium',
                )}
              >
                {option}
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
